import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { useTranslation } from '@acitra/i18n';
import { useAuthStore } from './stores/auth-store';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CompanySwitcher } from './components/CompanySwitcher';
import { Login } from './pages/Login';
import { CompanySelection } from './pages/CompanySelection';
import { TenantLoading } from './pages/TenantLoading';
import { AuthCallback } from './pages/AuthCallback';
import { Logout } from './pages/Logout';
import { Invoices } from './pages/Invoices';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { Settings } from './pages/Settings';
import { QBDisconnected } from './pages/QBDisconnected';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { Signup } from './pages/Signup';
import { useWebSocket } from './hooks/useWebSocket';

function Dashboard() {
  const { t } = useTranslation();
  const { getCurrentTenant } = useAuthStore();
  const tenant = getCurrentTenant();

  // Use companyInfo.companyName if available, fall back to tenant.name
  const companyName =
    (tenant as { companyInfo?: { companyName?: string } })?.companyInfo?.companyName ||
    tenant?.name;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold text-gray-900">{t('nav.dashboard')}</h1>
      {companyName && <p className="mt-2 text-gray-600">Welcome to {companyName}</p>}
    </div>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-xl font-bold text-primary-500">{t('common.appName')}</span>
              <div className="ml-4 border-l border-gray-200 pl-4">
                <CompanySwitcher />
              </div>
            </div>
            <nav className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-primary-500">
                {t('nav.dashboard')}
              </Link>
              <Link to="/invoices" className="text-gray-700 hover:text-primary-500">
                {t('nav.invoices')}
              </Link>
              <Link to="/approvals" className="text-gray-700 hover:text-primary-500">
                {t('nav.approvals')}
              </Link>
              <Link to="/settings" className="text-gray-700 hover:text-primary-500">
                {t('nav.settings')}
              </Link>
              <Link to="/logout" className="text-gray-700 hover:text-primary-500">
                Sign out
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main>{children}</main>
    </div>
  );
}

function App() {
  const { initialize, isLoading } = useAuthStore();

  // Initialize auth on app load
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Initialize WebSocket connection (connects when authenticated + tenant selected)
  useWebSocket();

  // Show loading spinner during initial auth check
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/invite/:token" element={<AcceptInvitation />} />
        <Route path="/signup" element={<Signup />} />

        {/* Semi-protected routes (need auth but not tenant) */}
        <Route path="/companies" element={<CompanySelection />} />
        <Route path="/tenant/:tenantId/loading" element={<TenantLoading />} />
        <Route path="/qb-disconnected" element={<QBDisconnected />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Invoices />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices/:invoiceId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <InvoiceDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/approvals"
          element={
            <ProtectedRoute>
              <AppLayout>
                <div className="p-8">Approvals</div>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Settings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
